/**
 * telemetry — Surface Layer (/01)
 *
 * Lightweight, auditable client telemetry. `track(name, props)` emits a typed
 * `TELEMETRY` bridge event (so the Swift host can record it) and keeps a bounded
 * in-memory ring buffer for inspection/flush. PII-safe by contract: callers pass
 * only event names + primitive, non-identifying props (route keys, counts, codes)
 * — never raw content, tokens, or emails.
 */

import { emit as emitBridgeEvent } from '@lib/webkit/messageHandler';

export type TelemetryProps = Record<string, string | number | boolean>;

export interface TelemetryRecord {
  name: string;
  props: TelemetryProps;
  at: number;
}

const BUFFER_LIMIT = 200;
const buffer: TelemetryRecord[] = [];

export function track(name: string, props: TelemetryProps = {}): void {
  const record: TelemetryRecord = { name, props, at: Date.now() };
  buffer.push(record);
  if (buffer.length > BUFFER_LIMIT) buffer.splice(0, buffer.length - BUFFER_LIMIT);

  try {
    emitBridgeEvent({ type: 'TELEMETRY', payload: { name, props } });
  } catch {
    // Bridge is a no-op in a plain browser; the buffer + console still record.
  }
  if (typeof console !== 'undefined' && typeof console.debug === 'function') {
    console.debug('[telemetry]', name, props);
  }
}

/** A snapshot of the in-memory telemetry buffer (most recent last). */
export function getTelemetryBuffer(): readonly TelemetryRecord[] {
  return buffer.slice();
}
